import * as THREE from "three";

interface MaterialOptions {
  name?: string;
  [key: string]: any;
}

export class MaterialManager {
  private static instance: MaterialManager;
  private materials: Map<string, THREE.Material>;
  private textureLoader: THREE.TextureLoader;

  public constructor() {
    this.initialize();
    this.materials = new Map();
    this.textureLoader = new THREE.TextureLoader();
    MaterialManager.instance = MaterialManager.instance || this;
    return MaterialManager.instance;
  }

  private initialize(): void {
    if (!MaterialManager.instance) {
      MaterialManager.instance = this;
    }
  }

  public createMaterial(
    type: string,
    options: MaterialOptions = {},
  ): THREE.Material {
    const key = options.name || this.generateMaterialKey(type);

    if (this.materials.has(key)) {
      console.log("duplicated material key", key);
      return this.materials.get(key)!;
    }
    const material = new THREE[type](options);
    material.name = key;
    this.materials.set(key, material);
    return material;
  }

  public getMaterial(name: string): THREE.Material | null {
    return this.materials.get(name) || null;
  }

  public updateMaterial(
    name: string,
    options: MaterialOptions,
  ): THREE.Material | null {
    const material = this.getMaterial(name);
    if (!material) {
      console.log("No material found to update");
      return null;
    }
    Object.assign(material, options);
    material.needsUpdate = true;
    return material;
  }

  public cloneMaterial(
    material: THREE.Material,
    newKeyName?: string,
  ): THREE.Material {
    if (!(material instanceof THREE.Material)) {
      throw new Error("Input must be a THREE.Material instance");
    }
    const newKey = newKeyName || `clone_${material.name}_${Date.now()}`;
    const clonedMaterial = material.clone();
    clonedMaterial.name = newKey;
    this.materials.set(newKey, clonedMaterial);
    return clonedMaterial;
  }

  public disposeMaterial(name: string): void {
    const material = this.getMaterial(name);
    if (!material) return;
    material.dispose();
    this.materials.delete(name);
  }

  public preloadTextures(): Promise<THREE.Texture[]> {
    return Promise.all([
      this.textureLoader.load("path/to/common/texture1.jpg"),
      this.textureLoader.load("path/to/common/texture2.jpg"),
    ]);
  }

  private generateMaterialKey(type: string): string {
    return `material_${type}_${Date.now()}`;
  }
}
